import React from 'react';
import { useSelector } from 'react-redux';
import { selectMapColors } from '../../store/data/data.selector';
import { selectIsCountyLevel } from '../../store/map/map.selector';
import {
  selectAttributeId,
  selectAttributeInfo,
  selectHazardMetric,
  selectIsClimate,
} from '../../store/options/options.selector';
import { selectCBFriendly } from '../../store/ui/ui.selector';

import { getAttributeText } from '../../utilities/mapPopupText';
import formatNumber from '../../utilities/formatNumber';

import './legend.styles.scss';

const BACK_IMG = `linear-gradient(
  270deg,
  hsla(0,100%,50%,0.95) 0%,
  hsla(0,100%,50%,0.95) 6%,
  hsla(10,100%,50%,0.95) 6%,
  hsla(10,100%,50%,0.95) 19%,
  hsla(20,100%,50%,0.95) 19%,
  hsla(20,100%,50%,0.95) 31%,
  hsla(30,100%,50%,0.95) 31%,
  hsla(30,100%,50%,0.95) 44%,
  hsla(39.83, 99.16%, 53.53%,0.95) 44%,
  hsla(39.83, 99.16%, 53.53%,0.95) 56%,
  hsla(39.3, 92.43%, 63.73%,0.95) 56%,
  hsla(39.3, 92.43%, 63.73%,0.95) 69%,
  hsla(39.13, 86.47%, 73.92%,0.95) 69%,
  hsla(39.13, 86.47%, 73.92%,0.95) 81%,
  hsla(37.85, 80.25%, 84.12%,0.95) 81%,
  hsla(37.85, 80.25%, 84.12%,0.95) 94%,
  hsla(40,0%,50%,0.95) 94%,
  hsla(40,0%,50%,0.95) 100%
  )`;

export default function Legend() {
  const isClimate = useSelector(selectIsClimate);
  const { mapColorRange } = useSelector(selectMapColors);
  const attributeId = useSelector(selectAttributeId);
  const attributeInfo = useSelector(selectAttributeInfo);
  const hazardMetric = useSelector(selectHazardMetric);
  const cbFriendly = useSelector(selectCBFriendly);
  const isCountyLevel = useSelector(selectIsCountyLevel);

  let legendLowerColor, legendMidColor, legendUpperColor, titleText;
  let max = 0;
  let min = 0;
  let binned = false;

  const { unit } = getAttributeText(
    isClimate,
    isClimate ? attributeId : hazardMetric
  );

  if (mapColorRange) {
    const range = isCountyLevel ? mapColorRange.county : mapColorRange.state;
    min = formatNumber(range.min);
    max = formatNumber(range.max);
  }

  if (isClimate) {
    if (attributeInfo.precip) {
      legendLowerColor = cbFriendly
        ? ['hsla(0,100%,100%,0.95)', 'hsla(103,63.8%,41.2%,0.95)']
        : ['hsla(135,100%,75%,0.95)', 'hsla(135,100%,0%,0.95)'];
      legendMidColor = 'hsla(0,100%,100%,0.95)';
      legendUpperColor = cbFriendly
        ? ['hsla(0,100%,100%,0.95)', 'hsla(323,76.3%,46.3%,0.95)']
        : ['hsla(40,100%,75%,0.95)', 'hsla(40,100%,0%,0.95)'];
    } else {
      if (attributeId === 5 || attributeId === 6) {
        legendLowerColor = [
          'hsla(210,100%,90%,0.95)',
          'hsla(210,100%,25%,0.95)',
        ];
        legendMidColor = 'hsla(0,100%,100%,0.95)';
        legendUpperColor = ['hsla(0,100%,90%,0.95)', 'hsla(0,100%,25%,0.95)'];
      } else {
        legendLowerColor = ['hsla(0,100%,90%,0.95)', 'hsla(0,100%,25%,0.95)'];
        legendMidColor = 'hsla(0,100%,100%,0.95)';
        legendUpperColor = [
          'hsla(210,100%,90%,0.95)',
          'hsla(210,100%,25%,0.95)',
        ];
      }
    }

    if (attributeInfo['dayUnits']) {
      titleText = `Trend (${unit.slice(10, 14)}/Decade)`;
    } else {
      titleText = `${unit.charAt(0).toUpperCase() + unit.slice(1, -1)}/Decade)`;
    }
  } else {
    binned = hazardMetric.includes('damage') ? true : false;
    titleText = unit.charAt(0).toUpperCase() + unit.slice(1);

    legendLowerColor = ['hsla(40,51%,50%,0.95)', 'hsla(40,100%,50%,0.95)'];
    legendMidColor = 'hsla(40,50%,50%,0.95)';
    legendUpperColor = ['hsla(40,49%,50%,0.95)', 'hsla(40,0%,50%,0.95)'];
  }

  return (
    <div className='legend-controls-container'>
      {max === 0 || max === '0' ? (
        <div id='no-legend'>No occurances reported</div>
      ) : (
        <div id='legend-wrapper'>
          <div id='legend'>
            {binned ? (
              <div
                id='legend-color'
                style={{
                  backgroundImage: BACK_IMG,
                }}
              >
                <div id='dividers'>
                  <div className='divider'></div>
                  <div className='divider'></div>
                  <div className='divider'></div>
                  <div className='divider'></div>
                  <div className='divider'></div>
                  <div className='divider'></div>
                  <div className='divider'></div>
                  <div className='divider'></div>
                </div>
              </div>
            ) : (
              <>
                <div
                  id='legend-color-lower'
                  style={{
                    background: `linear-gradient(270deg,
                    ${legendMidColor} 0%,
                    ${legendUpperColor[0]} 1%,
                    ${legendUpperColor[1]} 100%)`,
                  }}
                ></div>

                <div
                  id='legend-color-upper'
                  style={{
                    background: `linear-gradient(270deg,${legendLowerColor[1]} 0%,
                    ${legendLowerColor[0]} 99%,
                    ${legendMidColor} 100%)`,
                  }}
                ></div>
              </>
            )}
          </div>

          {binned ? (
            <div id='legend-units'>
              {/* <div className="binned-legend-text zero">$0</div> */}
              <div className='binned-legend-text one'>
                <span>$</span>1
              </div>
              <div className='binned-legend-text two'>
                <span>$</span>1k
              </div>
              <div className='binned-legend-text three'>
                <span>$</span>10k
              </div>
              <div className='binned-legend-text four'>
                <span>$</span>100k
              </div>
              <div className='binned-legend-text five'>
                <span>$</span>1M
              </div>
              <div className='binned-legend-text six'>
                <span>$</span>10M
              </div>
              <div className='binned-legend-text seven'>
                <span>$</span>100M
              </div>
              <div className='binned-legend-text eight'>
                <span>$</span>1B
              </div>
              {/* <div className="binned-legend-text nine">$1B+</div> */}
            </div>
          ) : (
            <div id='legend-units'>
              <div id='legend-title'>{titleText}</div>
              <div className='legend-text'>{min}</div>
              {isClimate && <div className='legend-text'>0</div>}
              <div className='legend-text'>{max}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
